import { useState, type CSSProperties } from 'react'
import { formatShortDate, weekdayName } from '@app/shared'
import type { ISODate, LearningItem, LearningResource } from '@app/shared'

import { useToggleLearningItem, type LearningTrackSummary } from '../../lib/data'
import { hueFor, scheduleNote, type TrackHue } from './derive'
import { ChevronDown, ExternalLink, Tick } from './icons'

// ============================================================================
// TODAY — the top of the Learning page, and the reason the page exists.
//
// The owner's own words for this screen (2026-08-28): "I just go to the learning page and start
// learning as quickly as possible." So the first thing under the band is not a chart and not a
// percentage: it is TODAY'S ITEMS, each with a checkbox and a button that opens the exact thing to
// open. Everything that measures him — the rings, the pace line, the heatmap — sits below it.
//
// WHERE THE ROWS COME FROM, and why it is two reads and not one. `GET /api/learning/items?date=`
// answers "what does the plan want today", which is the honest question — but a track running
// AHEAD has already finished today's item, and a track BEHIND has nothing dated today at all.
// Both would show him an empty board on a day he has work to do. So each track that has no
// unfinished row for today contributes its `nextItem` instead, labelled with what the plan
// actually says about that item's date ("2 days ahead", "overdue since Mon"). Nothing is
// re-dated and nothing is invented: the row says which day it belongs to.
//
// THE RESOURCE BUTTON IS THE FEATURE. One click, new tab, the plan's own label on it. An item the
// plan gave no resource shows NO button — not a disabled one and not a fallback to the track's
// library, because a control that shrugs is worse than one that is honestly absent
// (CLAUDE.md → the app never fakes knowledge).
// ============================================================================

/** One startable row: an item, plus which question it is answering. */
interface Row {
  item: LearningItem
  /** `today` — the plan dates it today. `next` — the next unfinished item, offered instead. */
  origin: 'today' | 'next'
  hue: TrackHue
}

/**
 * Today's rows across every track.
 *
 * Exported and pure so the assembly rule above is checkable without a browser: "a track with
 * nothing open today offers its next item, and never twice" is a sentence a unit test can hold.
 */
export function boardRows(
  items: LearningItem[],
  summaries: LearningTrackSummary[],
  today: ISODate,
): Row[] {
  const rows: Row[] = []
  summaries.forEach((summary, index) => {
    const hue = hueFor(index)
    const own = items.filter((i) => i.trackId === summary.track.id && i.date === today)
    for (const item of own) rows.push({ item, origin: 'today', hue })
    if (own.some((i) => !i.done)) return
    const next = summary.nextItem
    if (!next || own.some((i) => i.id === next.id)) return
    rows.push({
      item: {
        id: next.id,
        trackId: summary.track.id,
        trackName: summary.track.name,
        unit: summary.currentUnit,
        title: next.title,
        // The day the PLAN gives it, not today. The chip beside the row says what that means.
        date: next.plannedDate,
        done: next.done,
        minutes: next.minutes,
        resource: next.resource,
        minutesEstimate: next.minutesEstimate,
        instructions: next.instructions,
      },
      origin: 'next',
      hue,
    })
  })
  // WHAT IS LEFT, FIRST. A finished row is still worth showing — the day's count is only honest if
  // it includes what is already done — but it belongs under the work, not above it. The sort is
  // stable, so within each half the tracks stay in plan order.
  return [...rows].sort((a, b) => Number(a.item.done) - Number(b.item.done))
}

export function TodayBoard({
  items,
  summaries,
  today,
}: {
  items: LearningItem[]
  summaries: LearningTrackSummary[]
  today: ISODate
}) {
  const rows = boardRows(items, summaries, today)
  const [openId, setOpenId] = useState<string | null>(null)
  const done = rows.filter((r) => r.item.done).length

  return (
    <section className="flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <header className="flex flex-none items-baseline gap-[9px]">
        <h2 className="text-[15px] font-semibold leading-none tracking-[-0.01em]">Today</h2>
        <span className="text-micro font-normal text-muted">
          {weekdayName(today)}, {formatShortDate(today)}
        </span>
        {rows.length > 0 && (
          <span className="ml-auto text-micro font-medium text-ink-2">
            {done} of {rows.length} done
          </span>
        )}
      </header>

      {/* Nothing planned is a REAL answer and it gets said in words. A rest day the plan wrote in
          is not a bug, and a screen that stayed blank would leave him wondering which it was. */}
      {rows.length === 0 && (
        <p className="text-body text-ink-3 text-pretty">
          No plan has an item for today, and every track is finished or has nothing left to offer.
        </p>
      )}

      <ul className="flex flex-col gap-[7px]">
        {rows.map((row) => (
          <ItemRow
            key={row.item.id}
            row={row}
            today={today}
            open={openId === row.item.id}
            onToggleOpen={() => setOpenId(openId === row.item.id ? null : row.item.id)}
          />
        ))}
      </ul>
    </section>
  )
}

function ItemRow({
  row,
  today,
  open,
  onToggleOpen,
}: {
  row: Row
  today: ISODate
  open: boolean
  onToggleOpen: () => void
}) {
  const { item, hue } = row
  const toggle = useToggleLearningItem()

  return (
    <li className="rounded-banner border border-fill bg-surface-2">
      <div className="flex items-center gap-[11px] px-[14px] py-[10px]">
        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-[11px]">
          <input
            type="checkbox"
            checked={item.done}
            onChange={(e) => toggle.mutate({ id: item.id, done: e.target.checked })}
            className="peer sr-only"
          />
          <span
            className={[
              'flex size-[19px] flex-none items-center justify-center rounded-keycap border-[1.8px] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
              item.done ? 'border-transparent bg-ink text-surface' : hue.border,
            ].join(' ')}
            aria-hidden
          >
            {item.done && <Tick size={12} strokeWidth={2.6} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="mb-[4px] flex items-center gap-[7px]">
              <span className={['text-[9.5px] font-semibold uppercase leading-none tracking-[0.08em]', hue.text].join(' ')}>
                {item.trackName}
              </span>
              {/* The row that is NOT today's says so, in the plan's own terms. */}
              {row.origin === 'next' && (
                <span className="text-[9.5px] font-normal leading-none text-microcopy">
                  next · {scheduleNote(item.date, today)}
                </span>
              )}
            </span>
            <span
              className={[
                'block truncate text-[13px] font-medium leading-[1.3]',
                item.done ? 'text-muted line-through' : '',
              ].join(' ')}
            >
              {item.title}
            </span>
          </span>
        </label>

        {/* THE PLAN'S estimate, marked as an estimate and absent when the plan made none. Never
            confused with the minutes actually logged, which live in the workspace below. */}
        {item.minutesEstimate !== null && (
          <span
            title={`The plan's own estimate for this item: ${item.minutesEstimate} minutes. Not a target and not a measurement.`}
            className="flex-none text-micro font-normal text-microcopy"
          >
            ~{item.minutesEstimate} min
          </span>
        )}

        {item.resource && <ResourceButton resource={item.resource} />}

        <button
          type="button"
          onClick={onToggleOpen}
          aria-expanded={open}
          title={open ? 'Close this item' : 'Instructions, the link, and logging the session'}
          className="mo-press flex size-[26px] flex-none items-center justify-center rounded-keycap border border-line text-muted transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-ink"
        >
          <ChevronDown
            size={13}
            className={['transition-transform duration-[var(--dur-fast)] ease-[var(--ease-out)]', open ? 'rotate-180' : ''].join(' ')}
          />
          <span className="sr-only">{open ? 'Close' : 'Open'} “{item.title}”</span>
        </button>
      </div>

      {open && <Workspace item={item} />}
    </li>
  )
}

/**
 * THE ONE-CLICK START. `target="_blank"` with `rel="noreferrer noopener"` — the tab that opens has
 * no handle back on this one, which matters because these urls come from an imported plan rather
 * than from anything the app wrote.
 */
function ResourceButton({ resource }: { resource: LearningResource }) {
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noreferrer noopener"
      title={`Open ${resource.label} in a new tab`}
      style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
      className="mo-ink mo-press flex max-w-[220px] flex-none items-center gap-[7px] rounded-tile bg-ink px-[13px] py-[7px] text-[11.5px] font-semibold leading-none text-surface"
    >
      <span className="truncate">{resource.label}</span>
      <ExternalLink size={12} className="flex-none" />
    </a>
  )
}

/**
 * THE ITEM WORKSPACE — what the plan said about this item, the link again in full, and the log.
 *
 * NO TIMER. The owner asked for a way to start fast, not for a stopwatch; minutes are typed if he
 * has them and left alone if he does not, which is the same contract the Worker keeps (an item
 * checked off without a duration HAS no duration, and zero would be a measurement nobody made).
 */
function Workspace({ item }: { item: LearningItem }) {
  const toggle = useToggleLearningItem()
  const [minutes, setMinutes] = useState('')
  const [note, setNote] = useState('')

  const parsed = Number.parseInt(minutes, 10)
  const validMinutes = Number.isFinite(parsed) && parsed > 0 && parsed <= 1440 ? parsed : null
  const canLog = validMinutes !== null || note.trim().length > 0

  return (
    <div className="flex flex-col gap-[11px] border-t border-line-5 px-[14px] py-3">
      {item.instructions ? (
        <p className="max-w-[720px] text-[12px] leading-[1.5] text-ink-2 text-pretty">
          {item.instructions}
        </p>
      ) : null}

      {item.resource ? (
        <a
          href={item.resource.url}
          target="_blank"
          rel="noreferrer noopener"
          className="mo-ink mo-press flex items-center gap-[9px] rounded-card border border-line bg-surface px-[11px] py-[8px]"
        >
          <span className="rounded-pill bg-fill-2 px-[9px] py-[3px] text-pill font-medium text-ink-3">
            {item.resource.kind}
          </span>
          <span className="min-w-0 flex-1 truncate text-label font-medium text-ink-2">
            {item.resource.label}
          </span>
          <ExternalLink size={12} className="flex-none text-muted" />
        </a>
      ) : null}

      {/* An item the plan left bare says so rather than showing an empty panel. */}
      {!item.instructions && !item.resource && (
        <p className="text-[12px] leading-[1.5] text-ink-3">
          The plan gave this item a title and a date and nothing else — no link and no
          instructions.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-[9px]">
        <label className="flex items-center gap-[7px] text-micro font-medium text-muted">
          Minutes
          <input
            type="number"
            min={1}
            max={1440}
            inputMode="numeric"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            placeholder={item.minutes !== null ? String(item.minutes) : '—'}
            className="mo-field w-[72px] rounded-tile border border-line bg-surface px-[9px] py-[5px] text-[12px] text-ink"
          />
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="How did it go?"
          className="mo-field min-w-[180px] flex-1 rounded-tile border border-line bg-surface px-[9px] py-[5px] text-[12px] text-ink"
        />
        <button
          type="button"
          disabled={!canLog}
          onClick={() =>
            toggle.mutate({
              id: item.id,
              done: true,
              session: { minutes: validMinutes, note: note.trim() || null },
            })
          }
          style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
          className="mo-ink mo-press flex-none rounded-tile bg-ink px-[13px] py-[7px] text-[11.5px] font-semibold leading-none text-surface disabled:opacity-40"
        >
          {item.done ? 'Update session' : 'Done · log it'}
        </button>
        {/* What is already on the record, stated rather than implied by an empty field. */}
        <span className="text-micro font-normal text-microcopy">
          {item.done
            ? item.minutes !== null
              ? `Logged · ${item.minutes} min`
              : 'Logged · no duration'
            : 'Not logged yet'}
        </span>
      </div>
    </div>
  )
}

export function TodayBoardSkeleton() {
  return (
    <section className="flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <span className="sr-only">Loading…</span>
      <div className="h-[14px] w-[90px] rounded-full bg-fill" aria-hidden />
      <div className="h-[42px] rounded-banner bg-surface-2" aria-hidden />
      <div className="h-[42px] rounded-banner bg-surface-2" aria-hidden />
    </section>
  )
}
